import { z } from "zod";

import { MediaJobSchema, MediaJobWarningSchema } from "./media.schema";

export enum WizardSessionStatus {
	DRAFT = "DRAFT",
	COMPLETED = "COMPLETED",
	ARCHIVED = "ARCHIVED",
}

export enum WizardStepStatus {
	OPEN = "OPEN",
	IN_PROGRESS = "IN_PROGRESS",
	APPROVED = "APPROVED",
}

export enum SceneArtifactKind {
	IMAGE = "IMAGE",
	VIDEO = "VIDEO",
}

export enum SceneArtifactState {
	NONE = "NONE",
	GENERATING = "GENERATING",
	READY = "READY",
	APPROVED = "APPROVED",
	STALE = "STALE",
}

/** How one clip runs into the next — the mockup's "Übergänge" select. */
export enum VideoTransition {
	HARD_CUT = "HARD_CUT",
	CROSSFADE_04 = "CROSSFADE_04",
	FADE_BLACK = "FADE_BLACK",
}

export const VIDEO_TRANSITIONS = [
	VideoTransition.HARD_CUT,
	VideoTransition.CROSSFADE_04,
	VideoTransition.FADE_BLACK,
] as const;

export enum SubtitleStyle {
	NONE = "NONE",
	CLASSIC = "CLASSIC",
	WORD_HIGHLIGHT = "WORD_HIGHLIGHT",
}

export enum SubtitlePosition {
	BOTTOM_CENTER = "BOTTOM_CENTER",
	BOTTOM_LEFT = "BOTTOM_LEFT",
	TOP_CENTER = "TOP_CENTER",
}

export enum SubtitleSize {
	S = "S",
	M = "M",
	L = "L",
}

/** The order the tiles and dropdowns are offered in — the mockup's `SUBS` and its selects. */
export const SUBTITLE_STYLES = [
	SubtitleStyle.NONE,
	SubtitleStyle.CLASSIC,
	SubtitleStyle.WORD_HIGHLIGHT,
] as const;
export const SUBTITLE_POSITIONS = [
	SubtitlePosition.BOTTOM_CENTER,
	SubtitlePosition.BOTTOM_LEFT,
	SubtitlePosition.TOP_CENTER,
] as const;
export const SUBTITLE_SIZES = [SubtitleSize.S, SubtitleSize.M, SubtitleSize.L] as const;

/** What step 3 falls back to before the author has touched anything. */
export const DEFAULT_AUDIO_CONFIG = {
	musicTrackId: null,
	musicVolumePercent: 25,
	subtitleStyle: SubtitleStyle.CLASSIC,
	subtitlePosition: SubtitlePosition.BOTTOM_CENTER,
	subtitleSize: SubtitleSize.M,
} as const;

/** What step 5 falls back to before the author has touched the export settings. */
export const DEFAULT_EXPORT_CONFIG = { transition: VideoTransition.HARD_CUT } as const;

/**
 * The trim invariants, mirrored from the API's `video-timeline-clip` model.
 *
 * The editor clamps to them so the author cannot drag into a refusal — but the server
 * enforces them independently, and it is the server that decides. These numbers exist to
 * make the UI pleasant, not to make it correct.
 */
export const MIN_CLIP_SECONDS = 2;
export const CLIP_PRECISION_SECONDS = 0.1;
/** How far one press of a nudge button moves a handle — the mockup's ±0,2 s. */
export const CLIP_NUDGE_SECONDS = 0.2;

/** The six steps, and the lengths the wizard offers. Both are fixed by the flow itself. */
export const WIZARD_STEPS = [1, 2, 3, 4, 5, 6] as const;
export const TARGET_LENGTHS_SECONDS = [15, 30, 45, 60] as const;

/**
 * What the video is narrated and subtitled in — BCM360-329. The API validates against the
 * same list; the names the author reads are i18n, under `videoWizard.languages.*`.
 *
 * It governs what the viewer hears and reads, not the pipeline's working language: the
 * images are prompted in German whatever is chosen here, because a picture has none.
 */
export const NARRATION_LANGUAGES = ["DE", "EN"] as const;

/**
 * The six graphical styles of step 2. The ids are the API's — it validates `styleId`
 * against the same list and owns each style's prompt fragment; the labels the author reads
 * are i18n, under `videoWizard.steps.style.styles.*`.
 */
export const SCENE_STYLE_IDS = ["photo", "bw", "illu", "3d", "aqua", "doku"] as const;
export const DEFAULT_SCENE_STYLE_ID = "photo";

/** Where an uploaded style is in the one analysis that gives it a description. */
export enum SceneStyleState {
	ANALYSING = "ANALYSING",
	READY = "READY",
	FAILED = "FAILED",
}

/**
 * A style the author uploaded a picture for — BCM360-327.
 *
 * Its `id` is the `custom:<n>` string `styleId` itself takes, so choosing one is the same
 * PATCH as choosing `photo`: the tile is new, the selection is not. The picture is only
 * ever the tile's own preview — what the image model is given is `promptFragment`, the
 * German sentence a vision model read out of it.
 */
export const WizardSceneStyleSchema = z.object({
	id: z.string(),
	state: z.nativeEnum(SceneStyleState),
	promptFragment: z.string().nullable().optional(),
	imageUrl: z.string().nullable().optional(),
	lastError: z.string().nullable().optional(),
	/** True once the author rewrote the description, which a late analysis must not undo. */
	edited: z.boolean(),
});

/** Scene duration bounds — the API rejects anything outside, so the input clamps to them. */
export const MIN_SCENE_DURATION_SECONDS = 2;

/**
 * The ceiling to bound a scene by when the selected model has not said otherwise — the
 * same static bound `video-wizard.dto.ts` enforces, which is the longest ladder in the
 * registry rather than any one model's.
 *
 * The real ceiling is per model and comes from `maxSceneDurationSeconds` on the step-4
 * row; see `maxSceneDurationOf`. This is only what is used before the model list has
 * loaded, or for a model that publishes no ceiling of its own.
 */
export const MAX_SCENE_DURATION_SECONDS = 30;

export const VideoWizardStepSchema = z.object({
	step: z.number(),
	status: z.nativeEnum(WizardStepStatus),
	aiModelId: z.number().nullable().optional(),
	generationCount: z.number(),
	actualCostUsd: z.number(),
	estimateUsd: z.number(),
	approvedAt: z.string().nullable().optional(),
});

export const StoryboardVersionSchema = z.object({
	id: z.number(),
	versionNumber: z.number(),
	note: z.string().nullable().optional(),
	feedback: z.string().nullable().optional(),
	isActive: z.boolean(),
	createdAt: z.string().nullable().optional(),
});

export const SceneArtifactSchema = z.object({
	state: z.nativeEnum(SceneArtifactState),
	version: z.number(),
	assetUrl: z.string().nullable().optional(),
	costUsd: z.number(),
	jobId: z.number().nullable().optional(),
	feedback: z.string().nullable().optional(),
	/**
	 * Why the last job for this artifact failed, when nothing newer has succeeded. The
	 * socket carries the same text as a toast that is gone on the next render; this is the
	 * copy that survives a missed event. The API writes it in German whatever the UI
	 * language, so a code the web knows (`lastErrorCode`) is shown in its own words instead.
	 */
	lastError: z.string().nullable().optional(),
	lastErrorCode: z.string().nullable().optional(),
});

/**
 * The governance report for one scene — brief §9. Shown per scene in step 3, because
 * "the length, tone and language were checked" is half of what the step promises and an
 * unverifiable claim otherwise.
 */
export const NarrationValidationSchema = z.object({
	passed: z.boolean(),
	rules: z.array(
		z.object({
			rule: z.string(),
			passed: z.boolean(),
			detail: z.string().nullable().optional(),
		})
	),
	repairAttempts: z.number(),
	wordCount: z.number(),
	targetWordCount: z.number(),
});

export const StoryboardSceneSchema = z.object({
	id: z.number(),
	orderIndex: z.number(),
	title: z.string(),
	description: z.string().nullable().optional(),
	durationSeconds: z.number(),
	tags: z.array(z.string()),
	narrationText: z.string().nullable().optional(),
	narrationValidation: NarrationValidationSchema.nullable().optional(),
	narrationAssetUrl: z.string().nullable().optional(),
	/** Per-word timings of the take — what the cut preview's live subtitles are cut from. */
	narrationAlignment: z
		.array(z.object({ word: z.string(), startSeconds: z.number(), endSeconds: z.number() }))
		.nullable()
		.optional(),
	/**
	 * What one render of *this* scene costs. Server-computed: a per-second price cannot be
	 * turned into a per-scene figure without knowing the video model's rounding rule, and
	 * at step 4's prices this is the difference between a rounding error and a dollar.
	 */
	videoEstimateUsd: z.number().nullable().optional(),
	image: SceneArtifactSchema.nullable().optional(),
	video: SceneArtifactSchema.nullable().optional(),
});

export const WizardStoryboardSchema = z.object({
	activeVersionId: z.number().nullable().optional(),
	versions: z.array(StoryboardVersionSchema),
	scenes: z.array(StoryboardSceneSchema),
	totalDurationSeconds: z.number(),
});

export const WizardTimelineClipSchema = z.object({
	position: z.number(),
	storyboardSceneId: z.number(),
	inSeconds: z.number(),
	outSeconds: z.number(),
	/**
	 * The narration's own line: where the voice starts inside the cut, and which part of the
	 * take is used. Every default means "untouched" — the voice starting with the picture
	 * and running whole — so a cut made before the voice had a line of its own reads the
	 * same as it always did.
	 */
	narrationOffsetSeconds: z.number().nullable().optional(),
	narrationInSeconds: z.number().nullable().optional(),
	narrationOutSeconds: z.number().nullable().optional(),
});

/** The smallest slice of a take worth keeping — the server's grid, one step of it. */
export const MIN_NARRATION_SECONDS = 0.1;

/**
 * The export settings. Typed rather than opaque because step 5 renders every field of it —
 * and because the server merges a patch field by field and resets step 5 when anything in
 * here actually changes, so sending a partial object has to be safe by construction.
 */
export const WizardExportConfigSchema = z.object({
	transition: z.nativeEnum(VideoTransition).nullable().optional(),
	formatPresetIds: z.array(z.number()).nullable().optional(),
	/**
	 * Whether the finished render becomes the scenario's intro video. Absent means yes, so
	 * a session started before the choice existed behaves as it always did.
	 */
	useAsIntroVideo: z.boolean().nullable().optional(),
});

/** One shape the finished export was rendered in. */
export const WizardExportAssetSchema = z.object({
	formatPresetId: z.number(),
	label: z.string(),
	width: z.number(),
	height: z.number(),
	url: z.string(),
});

/**
 * The narration settings. Typed rather than opaque because step 3 renders every field of
 * it — and because the server merges a patch field by field, so sending a partial object
 * has to be safe by construction.
 */
export const WizardAudioConfigSchema = z.object({
	voiceId: z.string().nullable().optional(),
	voiceName: z.string().nullable().optional(),
	musicTrackId: z.number().nullable().optional(),
	musicVolumePercent: z.number().nullable().optional(),
	subtitleStyle: z.nativeEnum(SubtitleStyle).nullable().optional(),
	subtitlePosition: z.nativeEnum(SubtitlePosition).nullable().optional(),
	subtitleSize: z.nativeEnum(SubtitleSize).nullable().optional(),
});

/**
 * The rail's calculation frame — what a 30-second video is expected to cost. Display only:
 * it never blocks a call, and spend is capped at the provider.
 */
export const BudgetBandSchema = z.object({ minUsd: z.number(), maxUsd: z.number() });

export const WizardCostsSchema = z.object({
	totalActualUsd: z.number(),
	forecastUsd: z.number(),
	byStep: z.record(z.string(), z.number()),
	budgetBand: BudgetBandSchema,
});

/**
 * The whole wizard, as the server sees it. Every mutation answers with this, so the client
 * never computes an unlock, an approval or a price of its own.
 */
export const VideoWizardSchema = z.object({
	id: z.number(),
	courseId: z.number(),
	status: z.nativeEnum(WizardSessionStatus),
	version: z.number(),
	currentStep: z.number(),
	unlockedStep: z.number(),
	prompt: z.string().nullable().optional(),
	targetLengthSeconds: z.number(),
	styleId: z.string().nullable().optional(),
	styles: z.array(WizardSceneStyleSchema).nullable().optional(),
	narrationLanguage: z.string(),
	/** The author's standing brief for the script — what kind of script it should be. */
	narrationBrief: z.string().nullable().optional(),
	audioConfig: WizardAudioConfigSchema.nullable().optional(),
	exportConfig: WizardExportConfigSchema.nullable().optional(),
	steps: z.array(VideoWizardStepSchema),
	storyboard: WizardStoryboardSchema,
	timeline: z.array(WizardTimelineClipSchema),
	costs: WizardCostsSchema,
	narrationPreviewUrl: z.string().nullable().optional(),
	introCourseFileId: z.number().nullable().optional(),
	/** The finished video itself, so step 5 can play back what it just produced. */
	introCourseFileUrl: z.string().nullable().optional(),
	/** Whether the scenario actually serves the finished video as its intro. */
	introLinked: z.boolean().optional(),
	/** Every shape the last successful export produced. */
	exportAssets: z.array(WizardExportAssetSchema).nullable().optional(),
	/**
	 * The caption sidecar that export produced — one per export rather than one per shape,
	 * because a `.vtt` has no pixels in it. Null when the author asked for no subtitles, and
	 * for every export rendered before M7.
	 */
	exportSubtitleUrl: z.string().nullable().optional(),
	/** What that export wants the author to know — a truncated narration, say. */
	exportWarnings: z.array(MediaJobWarningSchema).nullable().optional(),
	activeJobs: z.array(MediaJobSchema),
});

export type VideoWizard = z.infer<typeof VideoWizardSchema>;
export type WizardSceneStyle = z.infer<typeof WizardSceneStyleSchema>;
export type VideoWizardStep = z.infer<typeof VideoWizardStepSchema>;
export type StoryboardVersion = z.infer<typeof StoryboardVersionSchema>;
export type StoryboardScene = z.infer<typeof StoryboardSceneSchema>;
export type SceneArtifact = z.infer<typeof SceneArtifactSchema>;
export type WizardCosts = z.infer<typeof WizardCostsSchema>;
export type WizardAudioConfig = z.infer<typeof WizardAudioConfigSchema>;
export type WizardExportConfig = z.infer<typeof WizardExportConfigSchema>;
export type WizardExportAsset = z.infer<typeof WizardExportAssetSchema>;
export type WizardTimelineClip = z.infer<typeof WizardTimelineClipSchema>;
export type NarrationValidation = z.infer<typeof NarrationValidationSchema>;

/** The API's error taxonomy, as far as the wizard UI has to branch on it. */
export enum MediaErrorCode {
	VALIDATION = "VALIDATION",
	DOWNSTREAM_APPROVED = "DOWNSTREAM_APPROVED",
	STALE_VERSION = "STALE_VERSION",
	STEP_PRECONDITION = "STEP_PRECONDITION",
	STEP_LOCKED = "STEP_LOCKED",
	NARRATION_LANGUAGE = "NARRATION_LANGUAGE",
	NARRATION_LENGTH = "NARRATION_LENGTH",
	NARRATION_TONE = "NARRATION_TONE",
	NARRATION_SAFETY = "NARRATION_SAFETY",
	PROVIDER = "PROVIDER",
	/** The provider's safety layer refused the prompt — terminal, and the author's to fix. */
	PROVIDER_MODERATED = "PROVIDER_MODERATED",
	/**
	 * The provider refused the input image — a first frame that looks like a real person.
	 * Another model or another image fixes it; rephrasing does not.
	 */
	PROVIDER_IMAGE_REFUSED = "PROVIDER_IMAGE_REFUSED",
	/** The session was archived (BCM360-375) and is never written again. */
	SESSION_ARCHIVED = "SESSION_ARCHIVED",
	/** Archiving waits for every job of the session to settle. */
	WORK_IN_FLIGHT = "WORK_IN_FLIGHT",
}

/** The governance rejections, which name a scene and a rule rather than a system fault. */
export const NARRATION_ERROR_CODES = [
	MediaErrorCode.NARRATION_LANGUAGE,
	MediaErrorCode.NARRATION_LENGTH,
	MediaErrorCode.NARRATION_TONE,
	MediaErrorCode.NARRATION_SAFETY,
] as const;

export interface MediaErrorBody {
	statusCode?: number;
	code?: MediaErrorCode;
	message?: string;
	downstreamApproved?: boolean;
	steps?: number[];
}

/**
 * Reads the API's error body off an RTK Query rejection. The 409 DOWNSTREAM_APPROVED case
 * is not an error the user should see as one — it is the question "this resets approved
 * work, continue?", which the wizard answers by asking them.
 */
export const readMediaError = (error: unknown): MediaErrorBody | null => {
	const data = (error as { data?: unknown })?.data;

	if (!data || typeof data !== "object") {
		return null;
	}

	return data as MediaErrorBody;
};

/**
 * One archived session in the editor tab's read-only list (BCM360-375): when it was
 * archived, what it actually cost and how far it got. It cannot be reopened.
 */
export const ArchivedVideoWizardSchema = z.object({
	id: z.number(),
	archivedAt: z.string().nullable(),
	actualCostUsd: z.number(),
	unlockedStep: z.number(),
});

export const ArchivedVideoWizardListSchema = z.array(ArchivedVideoWizardSchema);

export type ArchivedVideoWizard = z.infer<typeof ArchivedVideoWizardSchema>;
